import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Form, Modal, Space, Spin, Typography, message } from 'antd';
import { EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { useProductPropertiesById } from '@/modules/loan-product-settings/hooks/useProductProperties';
import { DevelopmentLoanAgreement } from './DevelopmentLoanAgreement';
import type {
  Borrower, LoanDetails, Security, RepaymentItem,
  BorrowerAcceptance, SaccosApproval, AcceptanceCertificate,
} from './DevelopmentLoanAgreement';
import { useQuery } from '@tanstack/react-query';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useLoanApplicationStore } from '@/modules/loans/stores/loan-application-store';
import { calculateInstallmentAmount } from '@/shared/api/client/loan-schedule';
import { useLoanFeeCalculation } from '@/modules/loans/hooks/useLoanFeeCalculation';

const { Text } = Typography;

interface LoanAgreementViewProps {
  data?: any;
  formikProps?: any;
  form?: any;
  product?: any;
  readOnly?: boolean;
  required?: boolean;
  mode?: 'maker_acceptance' | 'approver_review' | 'default';
  onEnabledChange?: (enabled: boolean) => void;
  onAcceptanceChange?: (accepted: boolean) => void;
}

const parseConfig = (value: unknown): any | null => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(String(value));
  } catch {
    return null;
  }
};

const getValueData = (props: LoanAgreementViewProps) =>
  props.formikProps?.values?.valueData || props.data?.valueData || props.data || {};

const fmtDate = (d?: string) => {
  if (!d) return new Date().toLocaleDateString('en-GB');
  try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; }
};

interface LiveApplicationData {
  authUser?: { fullname?: string; custNo?: string; phoneNo?: string; email?: string; idCard?: string; branch?: string; };
  loanAmount?: number;
  loanPeriod?: number;
  grossSalary?: number;
  netSalary?: number;
  purpose?: string;
  product?: any;
  selectedApprovers?: any[];
  monthlyInstallment?: number;
  processingFee?: number;
  loanBalanceData?: any;
}

const buildAgreementProps = (
  valueData: any,
  config: any,
  accepted: boolean,
  acceptedAt: string,
  live?: LiveApplicationData,
) => {
  const today = new Date().toLocaleDateString('en-GB');
  const validUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB');

  const isLiveMode = !!live?.authUser;

  const loanId = valueData.loanId ?? valueData.id ?? '';
  const branch = live?.authUser?.branch ?? valueData.branch ?? '';

  const borrower: Borrower = {
    name: live?.authUser?.fullname ?? valueData.customerName ?? valueData.name ?? 'Member Name',
    memberNumber: live?.authUser?.custNo ?? valueData.custNo ?? '-',
    mobileNumber: live?.authUser?.phoneNo ?? valueData.phoneNo ?? '-',
    email: live?.authUser?.email ?? valueData.email ?? '-',
    nida: live?.authUser?.idCard ?? valueData.idNo ?? valueData.idCard ?? '-',
  };

  const rawAmount = live?.loanAmount ?? valueData.recommendedLoanAmt ?? valueData.loanAmt ?? valueData.loanAmount ?? 0;
  const rawPeriod = live?.loanPeriod ?? valueData.recommendedLnPeriod ?? valueData.lnPeriod ?? valueData.loanPeriod ?? 0;
  const rawRate = live?.product?.loanRate ?? live?.product?.rate ?? valueData.intRate ?? valueData.interestRate ?? 0;
  const installment = live?.monthlyInstallment ?? valueData.newInstallment ?? valueData.currentInstallment ?? 0;
  const procFee = live?.processingFee ?? valueData.processingFee;
  const topupAmount = live?.loanBalanceData?.unpaidPrincipal ?? valueData.unpaidPrincipal ?? 0;
  const isTopup = !!(live?.loanBalanceData?.activeLoanBehavior === 'ALLOW_TOPUP' || valueData.topupMode);
  const isRefinance = !!(live?.loanBalanceData?.activeLoanBehavior === 'ALLOW_REFINANCE' || valueData.refinanceMode);

  const loan: LoanDetails = {
    reference: loanId ? `${branch}.${loanId}` : (valueData.loanNumber ?? 'PENDING-APPLICATION'),
    amount: Number(rawAmount),
    existingBalance: isTopup || isRefinance ? Number(topupAmount) : undefined,
    totalExposure: isTopup ? Number(rawAmount) : undefined,
    purpose: live?.purpose ?? valueData.purpose ?? config?.facilityTerms?.facilityType ?? 'Development Loan',
    tenorMonths: Number(rawPeriod),
    disbursementSchedule: valueData.appliDate ? fmtDate(valueData.appliDate) : today,
    monthlyInstallment: Number(installment),
    disbursementAccount: valueData.acNo ?? '-',
    applicationFee: procFee != null ? `TZS ${Number(procFee).toLocaleString()}` : '-',
    insuranceFee: '-',
    deductionMethod: 'HR / Payroll Deduction',
    interestRate: Number(rawRate),
    effectiveAnnualInterestRate: Number(rawRate),
    interestComputationMethod: valueData.calType ?? 'Reducing Balance',
    totalAmountPayable: valueData.totalAmountToPay ?? 0,
    latePaymentPenalty: '5% per annum on overdue amount',
    debtRecoveryFees: 'As per SACCOS policy',
  };

  const buildGuarantors = () => {
    if (isLiveMode && live?.selectedApprovers?.length) {
      return live.selectedApprovers.flatMap((sel: any) =>
        (sel.approvers ?? []).map((a: any) => ({
          fullName: a.userFullName ?? a.userId ?? '-',
          memberNumber: a.userId ?? '-',
          maximumLiability: 0,
          mobile: '-',
          acceptanceRecord: 'Pending',
        }))
      );
    }
    return (valueData.guarantors ?? []).map((g: any) => ({
      fullName: g.fullName ?? g.name ?? '-',
      memberNumber: g.memberNumber ?? g.custNo ?? '-',
      maximumLiability: Number(g.maximumLiability ?? g.amount ?? 0),
      mobile: g.mobile ?? g.mobileNo ?? '-',
      acceptanceRecord: g.status ?? 'Pending',
    }));
  };

  const security: Security = {
    borrowerSavings: valueData.savings ?? 0,
    guarantors: buildGuarantors(),
    totalSecurity: valueData.savings ?? 0,
  };

  const borrowerAcceptance: BorrowerAcceptance = {
    accepted,
    statementVersion: '1.0',
    authenticationMethod: 'PORTAL_CHECKBOX',
    acceptedAt: acceptedAt || (accepted ? today : '-'),
    sessionAuditId: '-',
    ipDeviceInformation: '-',
  };

  const saccosApproval: SaccosApproval = {
    officerName: valueData.officerName ?? valueData.approvedBy ?? '-',
    officerUserId: valueData.officer ?? valueData.approBy ?? '-',
    officerRole: 'Credit Officer',
    approvedAt: valueData.approDate ? fmtDate(valueData.approDate) : '-',
  };

  const certificate: AcceptanceCertificate = {
    agreementVersion: '1.0',
    documentHash: '-',
    finalAgreementDelivery: 'Electronic',
    certificateStatus: 'VALID',
  };

  return {
    agreementId: loanId ? `AGR-${branch}-${loanId}` : 'AGR-PREVIEW',
    offerDate: valueData.appliDate ? fmtDate(valueData.appliDate) : today,
    offerValidFrom: today,
    offerValidUntil: validUntil,
    borrower,
    loan,
    security,
    repaymentSchedule: [] as RepaymentItem[],
    borrowerAcceptance,
    saccosApproval,
    certificate,
    agreementConfig: config,
  };
};

const LoanAgreementView: React.FC<LoanAgreementViewProps> = ({
  data,
  formikProps,
  form,
  product,
  readOnly = false,
  required = true,
  mode = 'default',
  onEnabledChange,
  onAcceptanceChange,
}) => {
  const valueData = getValueData({ data, formikProps });

  // Live application data — available when rendered in loan application flow
  const authUser = useAuthUser();
  const { selectedProduct, loanAmount, loanPeriod, selectedApprovers, loanBalanceData } = useLoanApplicationStore();

  // Determine active product (prop takes precedence, then store)
  const activeProduct = product ?? selectedProduct;
  const glNo = activeProduct?.glNo || valueData.glNo || '';

  // Watch live form values — useWatch returns undefined when form is not provided
  const watchedLoanAmount = Form.useWatch('loanAmount', form);
  const watchedLoanPeriod = Form.useWatch('loanPeriod', form);
  const watchedPurpose = Form.useWatch('purpose', form);
  const watchedGuarantorSelections = Form.useWatch('guarantorSelections', form);

  // Resolve live amounts — watched form values are most fresh, store as fallback
  const liveLoanAmount = Number(watchedLoanAmount ?? loanAmount ?? 0) || undefined;
  const liveLoanPeriod = Number(watchedLoanPeriod ?? loanPeriod ?? 0) || undefined;

  // Calculate installment amount live
  const { data: installmentData } = useQuery({
    queryKey: ['installment-preview', glNo, liveLoanAmount, liveLoanPeriod, activeProduct?.interestCalculation],
    queryFn: async () => {
      if (!liveLoanAmount || !liveLoanPeriod || !activeProduct) return null;
      const rate = (activeProduct.loanRate ?? activeProduct.rate ?? 0) / 100;
      const resp = await calculateInstallmentAmount({
        calculationMethod: activeProduct.interestCalculation || 'FR',
        balloonPeriod: 0,
        principalAmount: liveLoanAmount,
        interestRate: rate,
        periods: liveLoanPeriod,
      });
      return resp.data as number;
    },
    enabled: !!(glNo && liveLoanAmount && liveLoanPeriod && activeProduct),
    staleTime: 30_000,
  });
  const liveInstallment = installmentData ?? 0;

  // Fetch fees for processing fee display
  const { data: feeCalcResponse } = useLoanFeeCalculation(glNo || undefined, liveLoanAmount, liveLoanPeriod, loanBalanceData);
  const feeCalcData = (feeCalcResponse as any)?.data;
  const liveProcFee = feeCalcData?.totalDisbursementFees ?? undefined;

  // Resolve guarantors — watched form value (most fresh) or store
  const liveGuarantors = useMemo(() => {
    const raw = watchedGuarantorSelections ?? selectedApprovers ?? [];
    const parsed = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return []; } })() : raw;
    return Array.isArray(parsed) ? parsed : [];
  }, [watchedGuarantorSelections, selectedApprovers]);

  const { data: productResponse, isLoading } = useProductPropertiesById(glNo || undefined);
  const productProperties = productResponse?.data as any;
  const config = useMemo(
    () => parseConfig(activeProduct?.loanAgreementConfig || productProperties?.loanAgreementConfig || valueData.loanAgreementConfig),
    [activeProduct?.loanAgreementConfig, productProperties?.loanAgreementConfig, valueData.loanAgreementConfig]
  );
  const [accepted, setAccepted] = useState(
    Boolean(formikProps?.values?.agreementAccepted || form?.getFieldValue('agreementAccepted') || valueData.agreementAccepted)
  );
  const [acceptedAt, setAcceptedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const enabled = config?.enabled === true;
  const agreementTitle = config?.agreementTitle || 'Development Loan Offer and Electronic Acceptance Agreement';
  const acceptanceText = config?.acceptanceBoxText || 'I have read, understood and accept this loan agreement and its schedules.';

  useEffect(() => {
    const current = Boolean(formikProps?.values?.agreementAccepted || form?.getFieldValue('agreementAccepted') || valueData.agreementAccepted);
    setAccepted(current);
    onAcceptanceChange?.(current);
  }, [formikProps?.values?.agreementAccepted, form, valueData.agreementAccepted, onAcceptanceChange]);

  useEffect(() => {
    onEnabledChange?.(enabled);
  }, [enabled, onEnabledChange]);

  // Build live application data object for preview
  const liveApplicationData: LiveApplicationData | undefined = authUser ? {
    authUser: {
      fullname: authUser.fullname,
      custNo: authUser.custNo,
      phoneNo: authUser.phoneNo,
      email: authUser.email,
      idCard: authUser.idCard,
      branch: authUser.branch,
    },
    loanAmount: liveLoanAmount,
    loanPeriod: liveLoanPeriod,
    purpose: watchedPurpose,
    product: activeProduct,
    selectedApprovers: liveGuarantors,
    monthlyInstallment: liveInstallment || undefined,
    processingFee: liveProcFee,
    loanBalanceData,
  } : undefined;

  if (isLoading) return <Spin />;
  if (!enabled) return null;

  const agreementProps = buildAgreementProps(valueData, config, accepted, acceptedAt, liveApplicationData);

  const handlePrint = () => {
    const el = printRef.current;
    if (!el) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const styles = Array.from(document.styleSheets)
      .map((ss) => { try { return Array.from(ss.cssRules).map((r) => r.cssText).join('\n'); } catch { return ''; } })
      .join('\n');
    win.document.write(`<html><head><style>${styles}</style></head><body>${el.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const setAcceptance = async (checked: boolean) => {
    const now = new Date().toLocaleString('en-GB');
    setAccepted(checked);
    if (checked) setAcceptedAt(now);
    onAcceptanceChange?.(checked);
    const acceptance = {
      agreementAccepted: checked,
      agreementAcceptanceStatement: checked ? acceptanceText : undefined,
      agreementAcceptanceMethod: 'PORTAL_CHECKBOX',
      agreementConfigSnapshot: JSON.stringify(config),
    };
    formikProps?.setFieldValue?.('agreementAccepted', checked);
    formikProps?.setFieldValue?.('agreementAcceptanceStatement', acceptance.agreementAcceptanceStatement);
    formikProps?.setFieldValue?.('agreementAcceptanceMethod', acceptance.agreementAcceptanceMethod);
    formikProps?.setFieldValue?.('agreementConfigSnapshot', acceptance.agreementConfigSnapshot);
    form?.setFieldsValue(acceptance);

    const branch = valueData.branch || data?.branch;
    const loanId = valueData.loanId || data?.loanId;
    if (!branch || !loanId || readOnly) return;

    setSaving(true);
    try {
      await customInstance({
        url: `/api/v1/loans/${encodeURIComponent(branch)}/${encodeURIComponent(loanId)}/agreement`,
        method: 'PUT',
        data: {
          branch,
          loanId,
          status: checked ? 'ACCEPTED' : 'DRAFT',
          borrowerAccepted: checked,
          acceptanceStatement: checked ? acceptanceText : null,
          acceptanceMethod: 'PORTAL_CHECKBOX',
          acceptedAtNode: data?.nodeId || data?.state,
          configSnapshot: JSON.stringify(config),
        },
      });
      message.success(checked ? 'Loan agreement accepted' : 'Loan agreement acceptance withdrawn');
    } catch {
      message.error('Unable to save loan agreement acceptance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card
        title={agreementTitle}
        style={{ marginBottom: 16 }}
        extra={
          <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
            {accepted ? 'View Accepted Agreement' : 'Preview Agreement'}
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Electronic loan agreement"
          description={
            accepted
              ? 'You have accepted this loan agreement. Use the buttons below to view or print it.'
              : 'Click "Preview Agreement" to review the full designed agreement before accepting.'
          }
          style={{ marginBottom: 16 }}
        />

        <Checkbox
          checked={accepted}
          disabled={readOnly || saving}
          onChange={(e) => setAcceptance(e.target.checked)}
          style={{ marginTop: 8 }}
        >
          <Text strong>{acceptanceText}</Text>
        </Checkbox>

        {accepted && (
          <Space style={{ marginTop: 16 }}>
            <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
              View Agreement
            </Button>
            <Button icon={<PrinterOutlined />} onClick={() => { setPreviewOpen(true); setTimeout(handlePrint, 500); }}>
              Print Agreement
            </Button>
          </Space>
        )}

        {required && !accepted && !readOnly && (
          <Alert
            type="warning"
            showIcon
            message="Agreement acceptance is required before proceeding."
            style={{ marginTop: 12 }}
          />
        )}
      </Card>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width="90vw"
        style={{ top: 20 }}
        styles={{ body: { maxHeight: '82vh', overflowY: 'auto', padding: 0 } }}
        title={
          <Space>
            <span>Loan Agreement Preview</span>
            <Button size="small" icon={<PrinterOutlined />} onClick={handlePrint}>
              Print
            </Button>
          </Space>
        }
        footer={
          <Space>
            {!readOnly && (
              <Button
                type="primary"
                disabled={accepted || saving}
                onClick={() => { setAcceptance(true); setPreviewOpen(false); }}
              >
                {accepted ? 'Already Accepted' : 'Accept Agreement'}
              </Button>
            )}
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </Space>
        }
      >
        <div ref={printRef}>
          <DevelopmentLoanAgreement {...agreementProps} />
        </div>
      </Modal>
    </>
  );
};

export default LoanAgreementView;
